import { useEffect } from 'react';
import { Navigate, Outlet, createBrowserRouter, useLocation, type RouteObject } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import type { CurentiaDevice, Snapshot } from './lib/device';
import { worksInPractices } from './lib/practices';
import { Blocked, Home, Pending } from './screens/Home';
import { Login, Setup } from './screens/Login';
import { ConsentReview, MyCode, MyReadings, MyRecords, Sharing } from './screens/MyHealth';
import { PatientScreen, PatientsScreen } from './screens/Patients';
import { Pharmacies } from './screens/Pharmacies';
import { RegisterScreen } from './screens/Dispensing';
import { PracticeScreen } from './screens/Practice';
import { MyPrescription, MyPrescriptions, PracticePrescriptionScreen, PracticePrescriptionsScreen } from './screens/Prescriptions';
import { Join, NewPractice } from './screens/Practices';
import { RecoveryCode } from './screens/RecoveryCode';
import { Restore } from './screens/Restore';
import { Security } from './screens/Security';
import { SignUp } from './screens/SignUp';
import { Recover, Unlock } from './screens/Unlock';
import { Start, Welcome } from './screens/Welcome';
import { DeviceProvider, useDevice, useSnapshot } from './state';

/** Minutes of inactivity before the app locks itself and forgets its keys. */
export const AUTO_LOCK_MINUTES = 5;
const ACTIVITY = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

/** Where each state belongs. */
export function destination(state: Snapshot): string {
  switch (state.phase) {
    case 'loading':
      return '/';
    case 'fresh':
      return '/welcome';
    case 'needs_setup':
      return '/setup';
    case 'locked':
      return '/unlock';
    case 'unlocked':
      if (state.recoveryCode) return '/recovery-code';
      if (state.account?.status === 'pending') return '/pending';
      if (state.account?.status === 'rejected' || state.account?.status === 'suspended') return '/blocked';
      return '/home';
  }
}

/** Practice screens, for accounts that work in practices. */
const PRACTICE_PATH = /^\/(practices\/(new|[a-f0-9]{24}(\/(patients|prescriptions)(\/[0-9a-f-]{36})?|\/register)?)|join)$/;

/** A patient's own screens. */
const PATIENT_PATH = /^\/(my-code|records|readings|sharing|consents\/[a-f0-9]{24}|prescriptions(\/[0-9a-f-]{36})?)$/;

/** Which paths each state may visit. Anything else goes to its destination. */
export function allowed(state: Snapshot, path: string): boolean {
  switch (state.phase) {
    case 'loading':
      return true;
    case 'fresh':
      return path === '/welcome' || path === '/restore' || /^\/(start|signup|login)\/[a-z]+$/.test(path);
    case 'needs_setup':
      return path === '/setup';
    case 'locked':
      return path === '/unlock' || path === '/recover';
    case 'unlocked': {
      const home = destination(state);
      if (home === '/home') {
        const type = state.account?.type;
        return (
          path === home ||
          path === '/security' ||
          (worksInPractices(type) && PRACTICE_PATH.test(path)) ||
          (type === 'patient' && PATIENT_PATH.test(path)) ||
          (type === 'doctor' && path === '/pharmacies')
        );
      }
      if (home === '/pending') return path === home || path === '/security';
      return path === home;
    }
  }
}

function useIdleLock(active: boolean) {
  const device = useDevice();
  useEffect(() => {
    if (!active) return;
    const wait = AUTO_LOCK_MINUTES * 60_000;
    let timer = setTimeout(() => device.lock(), wait);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => device.lock(), wait);
    };
    for (const event of ACTIVITY) window.addEventListener(event, reset, { passive: true });
    return () => {
      clearTimeout(timer);
      for (const event of ACTIVITY) window.removeEventListener(event, reset);
    };
  }, [active, device]);
}

function Shell() {
  const state = useSnapshot();
  const location = useLocation();
  useIdleLock(state.phase === 'unlocked');
  if (state.phase === 'loading') {
    return (
      <p className="spinner" role="status">
        Opening Curentia…
      </p>
    );
  }
  if (!allowed(state, location.pathname)) return <Navigate to={destination(state)} replace />;
  return <Outlet />;
}

function Crash() {
  return (
    <main className="page">
      <h1>Something went wrong</h1>
      <p>Reload the page. Your records on this device are safe.</p>
    </main>
  );
}

/**
 * The verification team's pages, outside the device's own screens. They load
 * only when visited, and builds made with VITE_ADMIN_PAGES=false, as the phone
 * apps must be, leave them out entirely.
 */
const adminRoutes: RouteObject[] =
  import.meta.env.VITE_ADMIN_PAGES === 'false' ? [] : [{ path: '/admin/*', lazy: () => import('./admin/AdminApp'), errorElement: <Crash /> }];

const router = createBrowserRouter([
  ...adminRoutes,
  {
    path: '/',
    element: <Shell />,
    errorElement: <Crash />,
    children: [
      { index: true, element: null },
      { path: 'welcome', element: <Welcome /> },
      { path: 'start/:door', element: <Start /> },
      { path: 'signup/:door', element: <SignUp /> },
      { path: 'login/:door', element: <Login /> },
      { path: 'restore', element: <Restore /> },
      { path: 'setup', element: <Setup /> },
      { path: 'unlock', element: <Unlock /> },
      { path: 'recover', element: <Recover /> },
      { path: 'recovery-code', element: <RecoveryCode /> },
      { path: 'home', element: <Home /> },
      { path: 'pending', element: <Pending /> },
      { path: 'blocked', element: <Blocked /> },
      { path: 'security', element: <Security /> },
      { path: 'practices/new', element: <NewPractice /> },
      { path: 'practices/:id', element: <PracticeScreen /> },
      { path: 'practices/:id/patients', element: <PatientsScreen /> },
      { path: 'practices/:id/patients/:patientId', element: <PatientScreen /> },
      { path: 'practices/:id/prescriptions', element: <PracticePrescriptionsScreen /> },
      { path: 'practices/:id/prescriptions/:recordId', element: <PracticePrescriptionScreen /> },
      { path: 'practices/:id/register', element: <RegisterScreen /> },
      { path: 'join', element: <Join /> },
      { path: 'pharmacies', element: <Pharmacies /> },
      { path: 'my-code', element: <MyCode /> },
      { path: 'records', element: <MyRecords /> },
      { path: 'readings', element: <MyReadings /> },
      { path: 'sharing', element: <Sharing /> },
      { path: 'consents/:id', element: <ConsentReview /> },
      { path: 'prescriptions', element: <MyPrescriptions /> },
      { path: 'prescriptions/:id', element: <MyPrescription /> },
      { path: '*', element: null },
    ],
  },
]);

export function App({ device }: { device: CurentiaDevice }) {
  return (
    <DeviceProvider device={device}>
      <RouterProvider router={router} />
    </DeviceProvider>
  );
}
